/**
 * Two-pane shell shared by the calendar setup flow and the post-creation
 * builder: a scrolling stage on one side and a fixed-width tabbed panel with
 * a sticky footer on the other. Pure layout/chrome — no routing, form state,
 * or validation of its own; callers inject everything via props.
 * Spec: assets/handoffs/design_handoff_new_calendar_page/README.md §1, §4
 *
 * Deliberately not built on top of ../SidebarTab.js — that component is
 * Bootstrap-tab-pane-shaped and shared with AlertPage/other pages;
 * repurposing it here risks regressing those. This is a clean-room
 * replacement scoped to the calendar setup/builder shell only.
 *
 * @param {ReactNode} stage - left pane content (template grid, preview, or live widget)
 * @param {Array<{key:string,label:string,disabled?:boolean}>} tabs
 * @param {string} activeTab
 * @param {function=} onTabChange - (key) => void
 * @param {ReactNode} panelBody - right pane scrolling content
 * @param {ReactNode=} footer - sticky footer content; omit for states with no footer
 * @param {'left'|'right'='right'} panelSide - stub for the builder-controls redesign's left-panel variant (see README §1 "Open decision"); not implemented, only the default is styled
 * @param {boolean=false} panelHidden - when true, the panel is unmounted entirely and the stage reclaims its width, matching CalendarPage.js's own panel-toggle behavior
 */
import React from "react";
import classNames from "classnames";
import Tabs from "../../../theme/untitled/Tabs/Tabs";
import styles from "./BuilderShell.module.scss";

const BuilderShell = ({
  stage,
  tabs,
  activeTab,
  onTabChange,
  panelBody,
  footer,
  panelSide = "right",
  panelHidden = false,
  className,
  ...rest
}) => {
  return (
    <div
      className={classNames(styles.root, styles[`root--panel-${panelSide}`], className)}
      {...rest}
    >
      <div className={styles.stage}>{stage}</div>
      {!panelHidden && (
        <div className={styles.panel}>
          <Tabs
            variant="segmented"
            items={tabs}
            value={activeTab}
            onChange={onTabChange}
            className={styles.tabsStrip}
          />
          <div className={styles.panelBody}>{panelBody}</div>
          {footer && <div className={styles.footer}>{footer}</div>}
        </div>
      )}
    </div>
  );
};

export default BuilderShell;
